import {
  animalSearchPhase,
  showSearchErrorHint,
} from "@/api/cases/animalSearchPhase";
import type { CaseListItem } from "@/api/cases/CasesApi";
import { useMyCases } from "@/api/cases/useCases";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { Screen } from "@/ui/components/Screen";
import { StatusChip } from "@/ui/components/StatusChip";
import { ErrorState, LoadingState } from "@/ui/components/StateViews";
import { useRouter } from "expo-router";
import { MagnifyingGlassIcon } from "phosphor-react-native";
import { useEffect, useMemo, useState } from "react";
import { Text, TextInput, View } from "react-native";

const MAX_RESULTS = 50;

function normalize(value: string | null | undefined): string {
  return (value ?? "").toLowerCase();
}

/** Nur Fallfelder – niemals Finder-PII (Name/Telefon/E-Mail/Adresse). */
function matchesQuery(item: CaseListItem, query: string): boolean {
  return (
    normalize(item.name).includes(query) ||
    normalize(item.case_number).includes(query) ||
    normalize(item.care_site_name).includes(query) ||
    normalize(item.found_place_text).includes(query) ||
    normalize(item.found_place_name).includes(query)
  );
}

function animalTitle(item: CaseListItem): string {
  const num = item.case_number?.trim();
  const name = item.name?.trim() || "Unbenannt";
  return num ? `${num} – ${name}` : name;
}

function animalSubtitle(item: CaseListItem): string {
  const parts: string[] = [];
  if (item.care_site_name?.trim()) parts.push(item.care_site_name.trim());
  if (item.found_place_name?.trim()) parts.push(item.found_place_name.trim());
  else if (item.found_place_text?.trim()) parts.push(`Fundort: ${item.found_place_text.trim()}`);
  return parts.join(" · ");
}

export default function AnimalSearchScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { organizations } = useMyOrganizations();
  const { cases, loading, error, refetch } = useMyCases();
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (error) console.error("animal search", error);
  }, [error]);

  const query = search.trim().toLowerCase();
  const orgName = useMemo(() => {
    const names = new Map<string, string>();
    for (const org of organizations) {
      if (org.id && org.name) names.set(org.id, org.name);
    }
    return names;
  }, [organizations]);
  const showOrgBadge = new Set(cases.map((item) => item.organization_id)).size > 1;

  const results = useMemo(() => {
    if (!query) return [];
    return cases.filter((item) => matchesQuery(item, query));
  }, [cases, query]);

  const visibleResults = results.slice(0, MAX_RESULTS);
  const hasError = Boolean(error);
  const phase = animalSearchPhase({
    hasError,
    loading,
    caseCount: cases.length,
    query,
    resultCount: results.length,
  });
  const errorHint = showSearchErrorHint({
    hasError,
    caseCount: cases.length,
  });
  const retrySearch = () => {
    void refetch();
  };

  const openAnimal = (item: CaseListItem) => {
    router.push(routes.myAnimal(item.id) as never);
  };

  return (
    <Screen title="Suche" scroll contentStyle={{ paddingBottom: theme.space.xxl }}>
      <View style={{ gap: theme.space.md }}>
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.small,
          }}
        >
          Alle Fälle deiner Vereine – nach Name, Nummer, Pflegestelle oder Fundort.
        </Text>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: theme.space.sm,
            borderWidth: 1,
            borderColor: theme.color.border,
            backgroundColor: theme.color.surface,
            borderRadius: theme.radius.md,
            paddingHorizontal: theme.space.sm,
            paddingVertical: theme.space.xs,
          }}
        >
          <MagnifyingGlassIcon size={20} color={theme.color.muted} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            autoFocus
            placeholder="Name, Nummer, Pflegestelle oder Fundort…"
            placeholderTextColor={theme.color.muted}
            accessibilityLabel="Tiere durchsuchen"
            style={{
              flex: 1,
              paddingVertical: theme.space.sm,
              color: theme.color.text,
              fontSize: theme.fontSize.body,
            }}
          />
        </View>

        {errorHint ? (
          <ErrorState
            message="Die Tiere konnten nicht aktualisiert werden. Bereits geladene Treffer bleiben sichtbar."
            onRetry={retrySearch}
          />
        ) : null}

        {phase === "error" ? (
          <ErrorState
            message="Die Tiere konnten nicht geladen werden."
            onRetry={retrySearch}
          />
        ) : phase === "loading" ? (
          <LoadingState message="Lade Tiere…" />
        ) : phase === "prompt" ? (
          <EmptyState
            title="Suchbegriff eingeben"
            hint={
              cases.length > 0
                ? `${cases.length} ${cases.length === 1 ? "Tier ist" : "Tiere sind"} durchsuchbar.`
                : "Sobald Tiere angelegt sind, findest du sie hier."
            }
          />
        ) : phase === "empty" ? (
          <EmptyState
            title={`Keine Treffer für „${search.trim()}“.`}
            hint="Anderen Suchbegriff versuchen – gesucht wird in Name, Nummer, Pflegestelle und Fundort."
          />
        ) : (
          <View>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.tiny,
                fontWeight: "700",
                letterSpacing: 0.4,
                textTransform: "uppercase",
                paddingHorizontal: theme.space.md,
                paddingBottom: theme.space.xs,
              }}
            >
              {results.length} {results.length === 1 ? "Treffer" : "Treffer"}
              {results.length > MAX_RESULTS ? ` · erste ${MAX_RESULTS}` : ""}
            </Text>
            {visibleResults.map((item) => (
              <DenseRow
                key={item.id}
                title={animalTitle(item)}
                meta={animalSubtitle(item) || undefined}
                trailing={
                  showOrgBadge ? (
                    <StatusChip
                      label={orgName.get(item.organization_id) ?? "Verein"}
                      tone="neutral"
                      size="sm"
                    />
                  ) : undefined
                }
                onPress={() => openAnimal(item)}
              />
            ))}
          </View>
        )}
      </View>
    </Screen>
  );
}
